import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, RefreshCw, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { CornerAdjuster } from './CornerAdjuster';
import { loadScanner } from './opencvLoader';
import { canvasToFile, drawScaledToCanvas, loadImageElement } from './imageUtils';
import {
  applyEnhancement,
  computeOutputSize,
  detectInitialCorners,
  fallbackCorners,
  scaleCorners,
  warpToCorners,
  type Corners,
  type EnhanceMode,
} from './scanProcessing';

/**
 * Ersetzt den bisherigen Umweg über eine externe Scanner-App (Foto machen,
 * dort zuschneiden/optimieren, als PNG exportieren, dann hier hochladen):
 * Kamera direkt öffnen, Blattkanten automatisch erkennen (mit manueller
 * Korrektur-Möglichkeit), Perspektive geradeziehen und wahlweise
 * Farbe/Graustufen/S-W optimieren - Ergebnis kommt als normale `File` beim
 * Aufrufer an (der sie wie jede andere hochgeladene Datei weiterreicht, z.B.
 * an `/api/documents` oder den OCR-Parse-Endpoint).
 *
 * Über `ref.open()` gesteuert statt über eine `open`-Prop: der Aufrufer ruft
 * das direkt aus dem Klick-Handler seines eigenen "Scannen"-Buttons auf, was
 * synchron die native Kamera öffnet (kein Zwischenbildschirm mit einem
 * weiteren "Foto aufnehmen"-Button) - ein Browser öffnet einen
 * Datei-/Kamera-Dialog nur bei einer echten, synchronen Nutzeraktion, ein
 * über einen `useEffect` verzögerter Klick wäre hier zu unzuverlässig.
 *
 * Detailauflösung 900px, Ausgabeauflösung bis 2800px längste Kante -
 * iPhone-Fotos (4000px+) würden die clientseitige OpenCV-Verarbeitung sonst
 * unnötig verlangsamen, ohne dass ein Wiegeschein-Scan das bräuchte. 2800px
 * (vorher 2000px) ist bewusst höher angesetzt, weil bei 2000px auf kleiner
 * gedrucktem Text (v.a. das Bemerkungsfeld ganz unten) im "Scan (S/W)"-Modus
 * zu wenig reale Bilddetail für den adaptiven Schwellenwert übrig blieb -
 * das führte sowohl zu sichtbar pixeligen Kanten als auch zu OCR-Ausfällen
 * bei kleiner Schrift und vereinzelten Ziffern-Fehllesungen.
 */

const DETECT_MAX_DIM = 900;
const SOURCE_MAX_DIM = 2800;
const DEFAULT_ENHANCE_MODE: EnhanceMode = 'scan';

type Step =
  | { kind: 'idle' }
  | { kind: 'detecting' }
  | { kind: 'adjust'; sourceCanvas: HTMLCanvasElement; sourceUrl: string; corners: Corners }
  | { kind: 'enhance'; extractedCanvas: HTMLCanvasElement; mode: EnhanceMode }
  | { kind: 'error'; message: string };

interface Props {
  onCapture: (file: File) => void;
}

export interface DocumentScannerHandle {
  /** Öffnet direkt die Kamera (bzw. den Dateidialog auf Desktop) - keine Zwischenansicht. */
  open: () => void;
}

interface ScannerRuntime {
  cv: any;
  scanner: any;
}

export const DocumentScannerModal = forwardRef<DocumentScannerHandle, Props>(function DocumentScannerModal({ onCapture }, ref) {
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const runtimeRef = useRef<ScannerRuntime | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Für den Unmount-Cleanup unten - ein `useEffect` mit leerem
  // Dependency-Array würde sonst den Anfangswert von `step` einfangen statt
  // des aktuellen Stands.
  const stepRef = useRef(step);
  stepRef.current = step;

  useImperativeHandle(ref, () => ({
    open: () => {
      // Bewusst nicht auf das Laden warten - startet parallel zur
      // Kamera-Bedienung des Nutzers, damit es beim Bestätigen des Fotos
      // meist schon fertig ist.
      void loadScanner().then((runtime) => {
        runtimeRef.current = runtime;
      });
      fileInputRef.current?.click();
    },
  }));

  useEffect(() => {
    return () => {
      if (stepRef.current.kind === 'adjust') URL.revokeObjectURL(stepRef.current.sourceUrl);
    };
  }, []);

  async function ensureRuntime(): Promise<ScannerRuntime> {
    const runtime = runtimeRef.current ?? (await loadScanner());
    runtimeRef.current = runtime;
    return runtime;
  }

  async function handleFileSelected(file: File) {
    setStep({ kind: 'detecting' });
    try {
      const { img, revoke } = await loadImageElement(file);
      const sourceCanvas = drawScaledToCanvas(img, SOURCE_MAX_DIM);
      const detectCanvas = drawScaledToCanvas(img, DETECT_MAX_DIM);
      revoke();

      let corners: Corners | null = null;
      try {
        const { cv, scanner } = await ensureRuntime();
        corners = detectInitialCorners(cv, scanner, detectCanvas);
        if (corners) {
          corners = scaleCorners(corners, sourceCanvas.width / detectCanvas.width, sourceCanvas.height / detectCanvas.height);
        }
      } catch {
        corners = null;
      }
      if (!corners) corners = fallbackCorners(sourceCanvas.width, sourceCanvas.height);

      const previewBlob: Blob = await new Promise((resolve, reject) =>
        sourceCanvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Vorschau fehlgeschlagen.'))), 'image/jpeg', 0.9),
      );
      const sourceUrl = URL.createObjectURL(previewBlob);
      setStep({ kind: 'adjust', sourceCanvas, sourceUrl, corners });
    } catch (err) {
      setStep({ kind: 'error', message: err instanceof Error ? err.message : 'Foto konnte nicht verarbeitet werden.' });
    }
  }

  function handleCornersChange(corners: Corners) {
    setStep((prev) => (prev.kind === 'adjust' ? { ...prev, corners } : prev));
  }

  async function handleConfirmCorners() {
    if (step.kind !== 'adjust') return;
    const { sourceCanvas, corners, sourceUrl } = step;
    try {
      const { scanner } = await ensureRuntime();
      const { width, height } = computeOutputSize(corners);
      const extractedCanvas = warpToCorners(scanner, sourceCanvas, corners, width, height);
      URL.revokeObjectURL(sourceUrl);
      setStep({ kind: 'enhance', extractedCanvas, mode: DEFAULT_ENHANCE_MODE });
    } catch {
      setStep({ kind: 'error', message: 'Zuschneiden fehlgeschlagen.' });
    }
  }

  function handleModeChange(mode: EnhanceMode) {
    setStep((prev) => (prev.kind === 'enhance' ? { ...prev, mode } : prev));
  }

  async function handleFinish() {
    if (step.kind !== 'enhance') return;
    try {
      const { cv } = await ensureRuntime();
      const finalCanvas = applyEnhancement(cv, step.extractedCanvas, step.mode);
      // PNG (verlustfrei) eignet sich gut für "scan" (S/W-binarisiert - riesige
      // Flächen derselben Farbe, komprimiert winzig), aber sehr schlecht für
      // Farb-/Graustufen-Fotos mit kontinuierlichen Tonwerten (mehrere MB statt
      // einiger hundert KB) - dort ist JPEG die richtige Wahl. Das hatte zuvor
      // reale OCR-Scans über nginx' Standard-Upload-Limit (1MB) katapultiert,
      // die dann kommentarlos mit 413 abgewiesen wurden, bevor sie das Backend
      // überhaupt erreichten.
      const isBinarized = step.mode === 'scan';
      const mimeType = isBinarized ? 'image/png' : 'image/jpeg';
      const file = await canvasToFile(finalCanvas, `scan-${Date.now()}.${isBinarized ? 'png' : 'jpg'}`, mimeType, 0.92);
      onCapture(file);
      setStep({ kind: 'idle' });
    } catch {
      setStep({ kind: 'error', message: 'Export fehlgeschlagen.' });
    }
  }

  function handleClose() {
    if (step.kind === 'adjust') URL.revokeObjectURL(step.sourceUrl);
    setStep({ kind: 'idle' });
  }

  function retake() {
    fileInputRef.current?.click();
  }

  return createPortal(
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFileSelected(file);
        }}
      />

      {step.kind !== 'idle' && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-surface-950">
          <div className="flex items-center justify-between border-b border-surface-800 px-4 py-3">
            <h2 className="text-sm font-semibold text-ivory-100">Dokument scannen</h2>
            <button type="button" onClick={handleClose} aria-label="Schließen" className="rounded-md p-1.5 text-ivory-500 hover:bg-surface-800 hover:text-ivory-100">
              <X className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </div>

          <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-4">
            {step.kind === 'detecting' && (
              <div className="flex flex-col items-center gap-3">
                <Spinner className="h-8 w-8" />
                <p className="text-sm text-ivory-400">Blatt wird erkannt…</p>
              </div>
            )}

            {step.kind === 'adjust' && (
              <div className="flex w-full max-w-md flex-col items-center gap-3">
                <p className="text-center text-xs text-ivory-500">Ecken bei Bedarf per Ziehen anpassen.</p>
                <CornerAdjuster
                  imageUrl={step.sourceUrl}
                  imageWidth={step.sourceCanvas.width}
                  imageHeight={step.sourceCanvas.height}
                  corners={step.corners}
                  onChange={handleCornersChange}
                />
              </div>
            )}

            {step.kind === 'enhance' && (
              <div className="flex w-full max-w-md flex-col gap-4">
                <EnhancePreview cv={runtimeRef.current?.cv} sourceCanvas={step.extractedCanvas} mode={step.mode} />
                <div className="flex justify-center gap-2">
                  {(['color', 'gray', 'scan'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleModeChange(m)}
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                        step.mode === m ? 'bg-accent-500 text-surface-950' : 'bg-surface-800 text-ivory-300 hover:bg-surface-700'
                      }`}
                    >
                      {m === 'color' ? 'Farbe' : m === 'gray' ? 'Graustufen' : 'Scan (S/W)'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step.kind === 'error' && (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-danger-400">{step.message}</p>
                <Button type="button" variant="secondary" onClick={retake}>
                  Erneut versuchen
                </Button>
              </div>
            )}
          </div>

          {(step.kind === 'adjust' || step.kind === 'enhance') && (
            <div className="flex items-center justify-between gap-3 border-t border-surface-800 px-4 py-3">
              <Button type="button" variant="secondary" onClick={retake}>
                <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Neu
              </Button>
              {step.kind === 'adjust' ? (
                <Button type="button" onClick={() => void handleConfirmCorners()}>
                  Zuschneiden <Check className="h-4 w-4" strokeWidth={1.75} />
                </Button>
              ) : (
                <Button type="button" onClick={() => void handleFinish()}>
                  Übernehmen <Check className="h-4 w-4" strokeWidth={1.75} />
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </>,
    document.body,
  );
});

function EnhancePreview({ cv, sourceCanvas, mode }: { cv: any; sourceCanvas: HTMLCanvasElement; mode: EnhanceMode }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!cv || !canvasRef.current) return;
    const result = applyEnhancement(cv, sourceCanvas, mode);
    const target = canvasRef.current;
    target.width = result.width;
    target.height = result.height;
    const ctx = target.getContext('2d');
    ctx?.drawImage(result, 0, 0);
  }, [cv, sourceCanvas, mode]);

  return <canvas ref={canvasRef} className="w-full rounded-md border border-surface-800" />;
}
